import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { gameReviewService, imageService } from '../services';
import PopularReviews from './PopularReviews';
import Header from './Header';
import { useIsMobile } from '../hooks/useIsMobile';

// PopularNowBar: hiển thị danh sách game popular-now
const PopularNowBar = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPopularNow = async () => {
      try {
        const res = await gameReviewService.getPopularNow();
        setGames(res?.data?.content || []);
      } catch (err) {
        setError('Không thể tải danh sách hot games');
      } finally {
        setLoading(false);
      }
    };
    fetchPopularNow();
  }, []);

  if (loading) return <div style={{ margin: '16px 0', color: '#888' }}>Loading hot games...</div>;
  if (error) return <div style={{ margin: '16px 0', color: 'red' }}>{error}</div>;
  if (!games.length) return null;

  return (
    <div className="popular-now-bar" style={{ margin: '0 0 32px 0' }}>
      <div style={{ fontWeight: 700, fontSize: 24, color: '#181512', marginBottom: 18, textAlign: 'left' }}>Popular Now</div>
      <div className="popular-now-list" style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 14,
        alignItems: 'flex-start',
      }}>
        {games.map((game, idx) => (
          <div
            key={game.id || idx}
            className="popular-now-item"
            style={{
              display: 'flex',
              alignItems: 'center',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 2px 12px 0 rgba(0,0,0,0.07)',
              padding: '7px 16px 7px 10px',
              minHeight: 40,
              cursor: 'pointer',
              transition: 'box-shadow 0.18s, transform 0.18s',
              fontWeight: 600,
              fontSize: 16,
              color: '#222',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginBottom: 6,
              maxWidth: 1000,
            }}
            onClick={() => game.slug && navigate(`/get-game/${game.slug}`)}
            onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px 0 rgba(16,166,199,0.13)'}
            onMouseOut={e => e.currentTarget.style.boxShadow = '0 2px 12px 0 rgba(0,0,0,0.07)'}
          >
            <img
              src={imageService.getThumbnail(game.thumbnailPath)}
              alt={game.title}
              style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover', marginRight: 8, background: '#eee', flexShrink: 0 }}
            />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{game.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReviewDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [carouselImages, setCarouselImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef();
  const [showFullContent, setShowFullContent] = useState(false);
  const [activeTab, setActiveTab] = useState('review');
  const [gameData, setGameData] = useState(null);

  useEffect(() => {
    setLoading(true);
    const fetchGameData = async () => {
      try {
        const res = await gameReviewService.getBySlug(slug);
        const data = res?.data || null;
        setReview(data);
        if (data?.title) {
          document.title = data.title;
        }
        const imgs = Array.isArray(data?.imagePaths) ? data.imagePaths.slice(0, 4) : [];
        setCarouselImages([...imgs, ...imgs, ...imgs]);
      } catch (err) {
        setError('Không tìm thấy review!');
      } finally {
        setLoading(false);
      }
    };
    fetchGameData();
    return () => clearInterval(intervalRef.current);
  }, [slug]);

  useEffect(() => {
    if (carouselImages.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        const nextIndex = prev + 1;
        if (nextIndex >= 4) {
          setTimeout(() => setCurrentIndex(0), 400);
          return 4;
        }
        return nextIndex;
      });
    }, 3000);
    return () => clearInterval(intervalRef.current);
  }, [carouselImages.length]);

  // Add Google Analytics tracking for Fortnite review
  useEffect(() => {
    if (location.pathname === '/review-game/fortnite') {
      // Add Google Analytics script to head
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17208874212';
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-17208874212');
      `;
      document.head.appendChild(script2);

      return () => {
        document.head.removeChild(script1);
        document.head.removeChild(script2);
      };
    }
  }, [location.pathname]);

  const handleGetGame = () => {
    if (location.pathname === '/review-game/fortnite') {
      // Track conversion event
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17208874212/Xah2CMKMzNwaEOSp6o1A',
        'transaction_id': ''
      });
    }
    navigate(`/get-game/${slug}`);
  };

  if (loading) return <div>Đang tải...</div>;
  if (error) return <div>{error}</div>;
  if (!review) return <div>Không có dữ liệu review.</div>;

  return (
    <>
      <Header />
      <div className="blog-detail-container" style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'center', 
        alignItems: 'flex-start', 
        gap: 0, 
        maxWidth: 1200, 
        width: '100%', 
        margin: '0 auto',
        padding: isMobile ? '0 16px' : '0',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        <div className="blog-detail-box" style={{ 
          flex: 1, 
          maxWidth: 720, 
          width: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}>
          {carouselImages.length > 0 && (
            <div 
              className="review-carousel-outer"
              style={{
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                position: 'relative',
                margin: '0 auto 30px auto',
                height: isMobile ? '140px' : '180px',
                boxSizing: 'border-box'
              }}
            >
              <div 
                className="review-carousel-grid"
                style={{
                  display: 'flex',
                  width: `${carouselImages.length * 25}%`,
                  position: 'relative',
                  transform: `translateX(-${currentIndex * 25}%)`,
                  transition: 'transform 0.4s ease-in-out',
                  margin: '0 -5px',
                  boxSizing: 'border-box'
                }}
              >
                {carouselImages.map((img, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: '25%',
                      padding: '0 5px',
                      boxSizing: 'border-box',
                      flexShrink: 0
                    }}
                  >
                    <img
                      src={imageService.getThumbnail(img)}
                      alt={`Screenshot ${idx + 1}`}
                      className="review-carousel-img"
                      style={{
                        width: '100%',
                        height: isMobile ? '140px' : '250px',
                        objectFit: 'cover',
                        aspectRatio: '16/9',
                        borderRadius: '8px'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: 24,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '16px' : '0'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              width: isMobile ? '100%' : 'auto',
              background: '#fff',
              borderRadius: 16,
              padding: isMobile ? '16px' : '24px',
              boxShadow: '0 2px 12px 0 rgba(0,0,0,0.07)',
              flex: 1
            }}>
              {review.thumbnailPath && (
                <img
                  src={imageService.getThumbnail(review.thumbnailPath)}
                  alt={review.title}
                  style={{ 
                    width: isMobile ? 80 : 96, 
                    height: isMobile ? 80 : 96, 
                    borderRadius: 14, 
                    objectFit: 'cover', 
                    marginRight: isMobile ? 16 : 28,
                    background: '#eee'
                  }}
                />
              )}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center',
                flex: 1,
                alignItems: 'flex-start'
              }}>
                <h1 style={{ 
                  fontSize: isMobile ? 20 : 24, 
                  fontWeight: 700, 
                  margin: 0, 
                  color: '#000', 
                  textAlign: 'left',
                  lineHeight: 1.3
                }}>{review.title}</h1>
                {typeof review.rateAverage === 'number' && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    margin: '8px 0 0 0',
                    gap: 4
                  }}>
                    {[...Array(5)].map((_, i) => (
                      <span
                        key={i}
                        className="star"
                        style={{ 
                          color: i < Math.round(review.rateAverage) ? '#fbbf24' : '#e5e7eb', 
                          fontSize: isMobile ? '1.1rem' : '1.3rem'
                        }}
                      >★</span>
                    ))}
                  </div>
                )}
                <div style={{ 
                  color: '#bdbdbd', 
                  fontSize: isMobile ? 13 : 14, 
                  marginTop: 8,
                  textAlign: 'left',
                  width: '100%'
                }}>
                  All trademarks belong to their respective owners.
                </div>
              </div>
            </div>
            <button
              style={{
                padding: isMobile ? '12px 40px' : '14px 56px',
                background: '#10a6c7',
                color: '#fff',
                fontWeight: 700,
                fontSize: isMobile ? 13 : 14,
                border: '2px solid #10a6c7',
                borderRadius: 16,
                cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s, box-shadow 0.2s, transform 0.2s',
                outline: 'none',
                marginLeft: isMobile ? 0 : 56,
                boxShadow: '0 4px 24px 0 rgba(16,166,199,0.25), 0 0 0 4px rgba(16,166,199,0.10)',
                width: isMobile ? '100%' : 'auto',
                height: isMobile ? 'auto' : 56,
                position: 'relative',
                overflow: 'hidden'
              }}
              className="get-game-btn pulse-btn shimmer-btn"
              onClick={handleGetGame}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>GET GAME</span>
            </button>
          </div>
          <PopularNowBar />
          <div style={{ 
            display: 'flex', 
            gap: 0, 
            marginBottom: 0, 
            marginLeft: 0,
            width: isMobile ? '100%' : 'auto'
          }}>
            <button
              onClick={() => setActiveTab('review')}
              style={{
                padding: isMobile ? '8px 16px' : '10px 22px',
                background: activeTab === 'review' ? '#04c0f1' : '#fff',
                color: activeTab === 'review' ? '#fff' : '#49516f',
                fontWeight: 700,
                fontSize: isMobile ? 14 : 16,
                border: activeTab === 'review' ? 'none' : '2px solid #e5e7eb',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderBottom: activeTab === 'review' ? '2px solid #04c0f1' : '2px solid #e5e7eb',
                outline: 'none',
                cursor: 'pointer',
                boxShadow: activeTab === 'review' ? '0 2px 8px 0 rgba(4,192,241,0.08)' : 'none',
                transition: 'all 0.2s',
                flex: isMobile ? 1 : 'none'
              }}
            >
              Read Review
            </button>
            <button
              onClick={() => {
                setActiveTab('download');
                handleGetGame();
              }}
              style={{
                padding: isMobile ? '8px 16px' : '10px 22px',
                background: activeTab === 'download' ? '#04c0f1' : '#fff',
                color: activeTab === 'download' ? '#fff' : '#49516f',
                fontWeight: 700,
                fontSize: isMobile ? 14 : 16,
                border: activeTab === 'download' ? 'none' : '2px solid #e5e7eb',
                borderTopLeftRadius: 0,
                borderTopRightRadius: 8,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderBottom: activeTab === 'download' ? '2px solid #04c0f1' : '2px solid #e5e7eb',
                outline: 'none',
                cursor: 'pointer',
                boxShadow: activeTab === 'download' ? '0 2px 8px 0 rgba(4,192,241,0.08)' : 'none',
                transition: 'all 0.2s',
                flex: isMobile ? 1 : 'none'
              }}
            >
              Download
            </button>
          </div>
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: isMobile ? 16 : 28,
            marginTop: 0,
            background: '#fff',
            color: '#222',
            fontSize: isMobile ? 15 : 17,
            lineHeight: 1.7,
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.03)',
            textAlign: 'left',
            overflow: 'hidden',
            wordBreak: 'break-word',
            boxSizing: 'border-box',
            maxWidth: '100%'
          }}>
            <div
              className="blog-detail-content"
              style={{
                overflow: 'hidden',
                wordBreak: 'break-word',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
              dangerouslySetInnerHTML={{
                __html: showFullContent
                  ? review.content
                  : review.content.length > 1500
                    ? review.content.slice(0, 1500) + '...'
                    : review.content
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
              <button
                style={{
                  padding: isMobile ? '8px 24px' : '10px 32px',
                  background: '#10a6c7',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: isMobile ? 14 : 16,
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                  outline: 'none',
                }}
                onClick={() => setShowFullContent(v => !v)}
              >
                {showFullContent ? 'Show Less' : 'Read More'}
              </button>
            </div>
          </div>

          {/* Rating section */}
          <div style={{ marginTop: 40, textAlign: 'left' }}>
            <h2 style={{ fontWeight: 700, fontSize: isMobile ? 22 : 26, marginBottom: 24 }}>Rating</h2>
            {[
              { label: 'Graphics and Sound', value: review.graphicsAndSoundRating },
              { label: 'Controls', value: review.controlsRating },
              { label: 'Gameplay', value: review.gameplayRating },
              { label: 'Lasting Appeal', value: review.lastingAppealRating },
            ].map((item, idx) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
                <div style={{ width: isMobile ? 140 : 180, fontWeight: 600, fontSize: isMobile ? 14 : 16 }}>{item.label}</div>
                <div style={{ flex: 1, margin: '0 18px' }}>
                  <div style={{
                    width: '100%',
                    height: isMobile ? 8 : 12,
                    background: '#e5e7eb',
                    borderRadius: 8,
                    overflow: 'hidden',
                    position: 'relative',
                  }}>
                    <div style={{
                      width: `${(item.value / 5) * 100}%`,
                      height: '100%',
                      background: '#10a6c7',
                      borderRadius: 8,
                      transition: 'width 0.4s',
                    }} />
                  </div>
                </div>
                <div style={{ width: 32, textAlign: 'right', fontWeight: 700, color: '#10a6c7', fontSize: isMobile ? 16 : 18 }}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Pros and Cons section */}
          <div style={{ marginTop: 40, textAlign: 'left' }}>
            <div style={{ display: 'flex', gap: isMobile ? 24 : 48, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 12, fontSize: isMobile ? 16 : 18 }}>PROS</div>
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {Array.isArray(review.prosAndCons) && review.prosAndCons.filter(i => i.type === 1).map((item, idx) => (
                    <li key={idx} style={{ color: '#10a6c7', marginBottom: 8, fontSize: isMobile ? 14 : 16 }}>
                      <span style={{ color: '#222' }}>{item.content}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 12, fontSize: isMobile ? 16 : 18 }}>CONS</div>
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {Array.isArray(review.prosAndCons) && review.prosAndCons.filter(i => i.type === 2).map((item, idx) => (
                    <li key={idx} style={{ color: '#10a6c7', marginBottom: 8, fontSize: isMobile ? 14 : 16 }}>
                      <span style={{ color: '#222' }}>{item.content}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        {/* PopularReviews */}
        <div style={{ 
          minWidth: isMobile ? 'auto' : 340, 
          maxWidth: isMobile ? 720 : 380, 
          flex: isMobile ? 'none' : '0 0 360px',
          width: isMobile ? '100%' : 'auto',
          margin: isMobile ? '32px auto 0' : '0',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}>
          <PopularReviews />
        </div>
        <style>
          {`
            html, body {
              overflow-x: hidden;
              max-width: 100%;
              position: relative;
            }
            .get-game-btn:hover {
              background: linear-gradient(90deg, #10a6c7 60%, #1de9b6 100%);
              box-shadow: 0 8px 32px 0 rgba(16,166,199,0.35), 0 0 16px 4px #1de9b6;
              color: #fff;
              transform: scale(1.07);
            }
            .pulse-btn {
              animation: pulse-btn-anim 1.2s infinite;
            }
            .shimmer-btn::before {
              content: '';
              position: absolute;
              top: 0;
              left: -100%;
              width: 100%;
              height: 100%;
              background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
              animation: shimmer-anim 1.5s infinite;
              z-index: 1;
            }
            @keyframes pulse-btn-anim {
              0% { box-shadow: 0 4px 24px 0 rgba(16,166,199,0.25), 0 0 0 4px rgba(16,166,199,0.10); }
              50% { box-shadow: 0 8px 32px 0 rgba(16,166,199,0.45), 0 0 24px 8px #1de9b6; }
              100% { box-shadow: 0 4px 24px 0 rgba(16,166,199,0.25), 0 0 0 4px rgba(16,166,199,0.10); }
            }
            @keyframes shimmer-anim {
              0% { left: -100%; }
              50% { left: 100%; }
              100% { left: -100%; }
            }
            .blog-detail-content img {
              max-width: 100%;
              height: auto;
              display: block;
            }
            .blog-detail-content iframe {
              max-width: 100%;
              display: block;
            }
            .blog-detail-content table {
              max-width: 100%;
              overflow-x: auto;
              display: block;
            }
            .blog-detail-content * {
              max-width: 100%;
              box-sizing: border-box;
            }
          `}
        </style>
      </div>
    </>
  );
};

export default ReviewDetail; 